import type {
  StorageType,
  KeyPairType,
  Direction,
} from "@privacyresearch/libsignal-protocol-typescript";
import { createStore, get, set, del, keys } from "idb-keyval";
import type { UseStore } from "idb-keyval";

const DEFAULT_DB = "kaether-e2ee";
const DEFAULT_STORE = "kv";

const PREFIX = {
  IDENTITY_KEY_PAIR: "e2ee:idkp",
  REGISTRATION_ID: "e2ee:regid",
  PREKEY: "e2ee:pk:",
  SIGNED_PREKEY: "e2ee:spk:",
  SESSION: "e2ee:session:",
  REMOTE_IDENTITY: "e2ee:rid:",
  TRUSTED_IDENTITY: "e2ee:trusted:",
  /**
   * Where PrivateKeyVault persists its AES-GCM wrapping CryptoKey. The key is
   * generated with `extractable: false` and IndexedDB's structured-clone
   * preserves that flag — so the key persists across cold reloads but cannot
   * be exported by any same-origin script via `crypto.subtle.exportKey`.
   */
  VAULT_WRAPPING_KEY: "e2ee:vault:wrappingKey",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ab2b64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function b642ab(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function serializeKeyPair(kp: KeyPairType): {
  pubKey: string;
  privKey: string;
} {
  return { pubKey: ab2b64(kp.pubKey), privKey: ab2b64(kp.privKey) };
}

function deserializeKeyPair(kp: {
  pubKey: string;
  privKey: string;
}): KeyPairType {
  return { pubKey: b642ab(kp.pubKey), privKey: b642ab(kp.privKey) };
}

// ---------------------------------------------------------------------------
// PrivateKeyVault — non-extractable wrapping for the identity private key
//
// iOS parity: iOS stores the identity keypair in the Keychain with
//   `kSecAttrAccessibleAfterFirstUnlockThisDeviceOnly`, making the private key
//   bytes non-extractable and persistent across app launches.
//
// Web equivalent: we generate an AES-GCM wrapping key with `extractable: false`
//   and persist the `CryptoKey` object itself in IndexedDB. IndexedDB's
//   structured-clone of a CryptoKey preserves the `extractable: false` flag,
//   so the key survives cold reloads AND cannot be exported by any same-origin
//   script via `crypto.subtle.exportKey`. This matches iOS Keychain behaviour:
//   the bytes never leave the security boundary, but the key is durable.
//
// The identity private key bytes are AES-GCM encrypted by the wrapping key
//   before going into IndexedDB (separate row from the wrapping key itself).
//   To rotate (e.g. on logout), call `vault.reset(idb)` which deletes both the
//   wrapping key and the encrypted identity, forcing a fresh key generation
//   on the next `setIdentityKeyPair`.
// ---------------------------------------------------------------------------

export interface WrappedKeyRecord {
  /** AES-GCM IV (base64). */
  iv: string;
  /** AES-GCM ciphertext of the raw private key bytes (base64). */
  ct: string;
  /** Schema version — bump when the wire format changes. */
  v: 1;
}

export class PrivateKeyVault {
  /**
   * The non-extractable AES-GCM wrapping key. In-memory cache of the key
   * stored at `PREFIX.VAULT_WRAPPING_KEY` in IndexedDB. Loaded lazily on
   * first use; survives cold reloads via the IndexedDB row.
   */
  private wrappingKey: CryptoKey | null = null;

  /**
   * In-flight key-loading-or-generation promise. Guards against concurrent
   * callers both seeing `wrappingKey === null` and generating two different
   * keys, which would cause the second write to store ciphertext encrypted
   * with a key that the vault no longer holds.
   */
  private initPromise: Promise<CryptoKey> | null = null;

  /**
   * The IndexedDB store where the wrapping CryptoKey is persisted. Defaults
   * to the shared `kaether-e2ee` database. Tests pass an isolated store.
   */
  private readonly idb: UseStore;

  constructor(customStore?: UseStore) {
    this.idb = customStore ?? createStore(DEFAULT_DB, DEFAULT_STORE);
  }

  /**
   * Ensure the wrapping key is loaded (from IndexedDB) or generated (and
   * persisted to IndexedDB). Concurrent-safe and idempotent.
   *
   * The generated key has `extractable: false` and is stored as a CryptoKey
   * via structured-clone. IndexedDB preserves the non-extractable flag on
   * retrieval, so even after the key is read back, `exportKey` still throws.
   */
  private ensureWrappingKey(): Promise<CryptoKey> {
    if (this.wrappingKey) return Promise.resolve(this.wrappingKey);
    if (this.initPromise) return this.initPromise;
    this.initPromise = (async () => {
      const existing = await get<CryptoKey>(PREFIX.VAULT_WRAPPING_KEY, this.idb);
      if (existing && existing.algorithm && existing.type === "secret") {
        this.wrappingKey = existing;
        this.initPromise = null;
        return existing;
      }
      const fresh = await crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        /* extractable = */ false,
        ["encrypt", "decrypt"],
      );
      await set(PREFIX.VAULT_WRAPPING_KEY, fresh, this.idb);
      this.wrappingKey = fresh;
      this.initPromise = null;
      return fresh;
    })();
    return this.initPromise;
  }

  /**
   * Wrap raw private-key bytes with the non-extractable AES-GCM key.
   * Returns a `WrappedKeyRecord` safe to persist in IndexedDB.
   */
  async wrap(privKeyBytes: ArrayBuffer): Promise<WrappedKeyRecord> {
    const wk = await this.ensureWrappingKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      wk,
      privKeyBytes,
    );
    return { iv: ab2b64(iv.buffer), ct: ab2b64(ct), v: 1 };
  }

  /**
   * Unwrap a stored `WrappedKeyRecord` back to raw private-key bytes.
   * Throws if the vault's wrapping key has been rotated (cold reload).
   */
  async unwrap(record: WrappedKeyRecord): Promise<ArrayBuffer> {
    const wk = await this.ensureWrappingKey();
    const iv = new Uint8Array(b642ab(record.iv));
    const ct = b642ab(record.ct);
    return crypto.subtle.decrypt({ name: "AES-GCM", iv }, wk, ct);
  }

  /**
   * `true` when the vault has been initialised in this session (i.e. a
   * wrapping key is present in memory). Self-tests use this to confirm the
   * non-extractable key was actually generated.
   */
  get isInitialised(): boolean {
    return this.wrappingKey !== null;
  }

  /**
   * Expose the in-memory `CryptoKey` reference for self-test
   * `extractable` assertions. Not used by production code paths.
   */
  get wrappingCryptoKey(): CryptoKey | null {
    return this.wrappingKey;
  }

  /**
   * Reset to a fresh wrapping key. Deletes the persisted CryptoKey from
   * IndexedDB so the next `ensureWrappingKey` generates a new one. After
   * calling this, any `WrappedKeyRecord` encrypted with the old key
   * cannot be unwrapped. Intended for test teardown / logout / key rotation.
   */
  async reset(): Promise<void> {
    this.wrappingKey = null;
    this.initPromise = null;
    await del(PREFIX.VAULT_WRAPPING_KEY, this.idb);
  }
}

// ---------------------------------------------------------------------------
// Stored identity keypair record
// ---------------------------------------------------------------------------

interface StoredIdentityKeyPair {
  /** Plaintext public key (base64). Public keys are never secret. */
  pubKey: string;
  /**
   * AES-GCM wrapped private key bytes. Stored as a `WrappedKeyRecord` when
   * the vault is available; falls back to `{ legacy: string }` (plain base64)
   * when SubtleCrypto is unavailable (e.g. non-secure contexts in tests that
   * do not polyfill SubtleCrypto).
   */
  privKeyWrapped: WrappedKeyRecord | { legacy: string };
}

function isWrappedKeyRecord(
  r: WrappedKeyRecord | { legacy: string },
): r is WrappedKeyRecord {
  return (r as WrappedKeyRecord).v === 1;
}

// ---------------------------------------------------------------------------
// SignalProtocolStore
// ---------------------------------------------------------------------------

export class SignalProtocolStore implements StorageType {
  /**
   * The vault wraps the identity private key with a non-extractable AES-GCM
   * key that is persisted as a CryptoKey in IndexedDB — mirroring iOS
   * Keychain semantics (non-extractable AND durable across launches).
   */
  readonly vault: PrivateKeyVault;

  /**
   * The underlying idb-keyval store. Defaults to the shared kaether-e2ee
   * database. Pass a custom store during tests to achieve isolation without
   * touching the production database.
   */
  private readonly idb: UseStore;

  constructor(customStore?: UseStore) {
    this.idb = customStore ?? createStore(DEFAULT_DB, DEFAULT_STORE);
    this.vault = new PrivateKeyVault(this.idb);
  }

  // -------------------------------------------------------------------------
  // Identity key pair (hardened: private key is vault-wrapped)
  // -------------------------------------------------------------------------

  async getIdentityKeyPair(): Promise<KeyPairType | undefined> {
    const stored = await get<StoredIdentityKeyPair>(
      PREFIX.IDENTITY_KEY_PAIR,
      this.idb,
    );
    if (!stored) return undefined;

    const pubKey = b642ab(stored.pubKey);

    if (isWrappedKeyRecord(stored.privKeyWrapped)) {
      // The wrapping key persists in IndexedDB alongside the wrapped record,
      // so under normal conditions unwrap always succeeds. A throw here means
      // the wrapping-key row and identity row diverged (e.g. one was wiped
      // out-of-band, or an older record from before the persistent-vault
      // upgrade). We DO NOT silently regenerate identity — that would surface
      // to peers as an unexplained identity-key change, indistinguishable
      // from a key-compromise attack. Surface the error and let the caller
      // decide (re-link from another device, restore from SVR, etc.).
      const privKey = await this.vault.unwrap(stored.privKeyWrapped);
      return { pubKey, privKey };
    } else {
      // Legacy path: plain base64 — return as-is but log a deprecation hint.
      // Migrated automatically on the next `setIdentityKeyPair` call.
      const privKey = b642ab((stored.privKeyWrapped as { legacy: string }).legacy);
      return { pubKey, privKey };
    }
  }

  async setIdentityKeyPair(keyPair: KeyPairType): Promise<void> {
    let privKeyWrapped: WrappedKeyRecord | { legacy: string };
    try {
      privKeyWrapped = await this.vault.wrap(keyPair.privKey);
    } catch {
      // SubtleCrypto unavailable (e.g. http:// context) — store legacy.
      privKeyWrapped = { legacy: ab2b64(keyPair.privKey) };
    }
    const record: StoredIdentityKeyPair = {
      pubKey: ab2b64(keyPair.pubKey),
      privKeyWrapped,
    };
    await set(PREFIX.IDENTITY_KEY_PAIR, record, this.idb);
  }

  // -------------------------------------------------------------------------
  // Registration ID
  // -------------------------------------------------------------------------

  async getLocalRegistrationId(): Promise<number | undefined> {
    return get<number>(PREFIX.REGISTRATION_ID, this.idb);
  }

  async setLocalRegistrationId(id: number): Promise<void> {
    await set(PREFIX.REGISTRATION_ID, id, this.idb);
  }

  // -------------------------------------------------------------------------
  // Identity trust
  // -------------------------------------------------------------------------

  async isTrustedIdentity(
    identifier: string,
    identityKey: ArrayBuffer,
    _direction: Direction,
  ): Promise<boolean> {
    const trusted = await get<string>(
      PREFIX.TRUSTED_IDENTITY + identifier,
      this.idb,
    );
    if (!trusted) return true;
    return trusted === ab2b64(identityKey);
  }

  async saveIdentity(
    encodedAddress: string,
    publicKey: ArrayBuffer,
  ): Promise<boolean> {
    const existing = await get<string>(
      PREFIX.REMOTE_IDENTITY + encodedAddress,
      this.idb,
    );
    const newKey = ab2b64(publicKey);
    await set(PREFIX.REMOTE_IDENTITY + encodedAddress, newKey, this.idb);
    await set(PREFIX.TRUSTED_IDENTITY + encodedAddress, newKey, this.idb);
    return existing !== undefined && existing !== newKey;
  }

  async getRemoteIdentity(
    encodedAddress: string,
  ): Promise<ArrayBuffer | undefined> {
    const stored = await get<string>(
      PREFIX.REMOTE_IDENTITY + encodedAddress,
      this.idb,
    );
    return stored ? b642ab(stored) : undefined;
  }

  // -------------------------------------------------------------------------
  // PreKeys
  // -------------------------------------------------------------------------

  async loadPreKey(
    encodedAddress: string | number,
  ): Promise<KeyPairType | undefined> {
    const stored = await get<{ pubKey: string; privKey: string }>(
      PREFIX.PREKEY + encodedAddress,
      this.idb,
    );
    return stored ? deserializeKeyPair(stored) : undefined;
  }

  async storePreKey(
    keyId: number | string,
    keyPair: KeyPairType,
  ): Promise<void> {
    await set(PREFIX.PREKEY + keyId, serializeKeyPair(keyPair), this.idb);
  }

  async removePreKey(keyId: number | string): Promise<void> {
    await del(PREFIX.PREKEY + keyId, this.idb);
  }

  // -------------------------------------------------------------------------
  // Signed PreKeys
  // -------------------------------------------------------------------------

  async loadSignedPreKey(
    keyId: number | string,
  ): Promise<KeyPairType | undefined> {
    const stored = await get<{ pubKey: string; privKey: string }>(
      PREFIX.SIGNED_PREKEY + keyId,
      this.idb,
    );
    return stored ? deserializeKeyPair(stored) : undefined;
  }

  async storeSignedPreKey(
    keyId: number | string,
    keyPair: KeyPairType,
  ): Promise<void> {
    await set(PREFIX.SIGNED_PREKEY + keyId, serializeKeyPair(keyPair), this.idb);
  }

  async removeSignedPreKey(keyId: number | string): Promise<void> {
    await del(PREFIX.SIGNED_PREKEY + keyId, this.idb);
  }

  // -------------------------------------------------------------------------
  // Sessions
  // -------------------------------------------------------------------------

  async storeSession(
    encodedAddress: string,
    record: string,
  ): Promise<void> {
    await set(PREFIX.SESSION + encodedAddress, record, this.idb);
  }

  async loadSession(
    encodedAddress: string,
  ): Promise<string | undefined> {
    return get<string>(PREFIX.SESSION + encodedAddress, this.idb);
  }

  async removeSession(encodedAddress: string): Promise<void> {
    await del(PREFIX.SESSION + encodedAddress, this.idb);
  }

  async removeAllSessions(identifier: string): Promise<void> {
    const allKeys = await keys(this.idb);
    const prefix = PREFIX.SESSION + identifier;
    for (const key of allKeys) {
      if (typeof key === "string" && key.startsWith(prefix)) {
        await del(key, this.idb);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Wipe
  // -------------------------------------------------------------------------

  async clearAll(): Promise<void> {
    const allKeys = await keys(this.idb);
    for (const key of allKeys) {
      if (typeof key === "string" && key.startsWith("e2ee:")) {
        await del(key, this.idb);
      }
    }
    await this.vault.reset();
  }
}
